import React from 'react';
import { motion, type HTMLMotionProps } from 'framer-motion';
import { twMerge } from 'tailwind-merge';
import { spring } from '../../utils/motion';
export type ButtonVariant = 'accent' | 'primary' | 'secondary' | 'ghost' | 'outline';
export type ButtonSize = 'sm' | 'md' | 'lg';
interface ButtonProps extends Omit<HTMLMotionProps<'button'>, 'children'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: React.ReactNode;
  fullWidth?: boolean;
  children?: React.ReactNode;
}
const variantClass: Record<ButtonVariant, string> = {
  accent: 'bg-accent text-on-accent hover:brightness-[0.97]',
  primary: 'bg-ink text-bg hover:bg-ink/90',
  secondary: 'bg-surface-2 text-ink hover:bg-line',
  ghost: 'text-ink hover:bg-surface-2',
  outline: 'border border-line bg-surface text-ink hover:border-ink-3'
};
const sizeClass: Record<ButtonSize, string> = {
  sm: 'h-9 px-4 text-sm',
  md: 'h-11 px-5 text-[15px]',
  lg: 'h-14 px-7 text-base'
};
export const buttonBase = 'inline-flex items-center justify-center gap-2 rounded-full font-bold whitespace-nowrap select-none transition-[background-color,color,border-color,filter] duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-bg disabled:pointer-events-none disabled:opacity-50';
export function buttonClass(variant: ButtonVariant = 'primary', size: ButtonSize = 'md', extra?: string) {
  return twMerge(buttonBase, variantClass[variant], sizeClass[size], extra);
}
export function Button({
  variant = 'primary',
  size = 'md',
  icon,
  fullWidth,
  className,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  return <motion.button type={type} whileHover={{
    scale: 1.03
  }} whileTap={{
    scale: 0.96
  }} transition={spring.snappy} className={buttonClass(variant, size, twMerge(fullWidth ? 'w-full' : '', className as string))} {...rest} data-id="element-769" data-mp="17ds8ishpkifs">
      {icon}
      {children}
    </motion.button>;
}